import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/GoogleAuthContext';
import { UserPermissions, TabPermissions } from '../services/api-client';

// Tab display names
const TAB_LABELS: Record<keyof UserPermissions, string> = {
  dashboard: 'Dashboard',
  services: 'Services',
  permissions: 'Permissions',
  service_discovery: 'Service Discovery',
  settings: 'Settings',
  audit_log: 'Audit Log',
  generic_terms: 'Generic Terms',
  onboarding_survey: 'Onboarding Survey',
  user_management: 'User Management',
  content: 'Content',
  feature_flags: 'Feature Flags',
  staff: 'Staff',
};

// Action labels
const ACTION_LABELS: Record<keyof TabPermissions, string> = {
  view: 'View',
  edit: 'Edit',
  upload: 'Upload',
  sync: 'Sync',
  publish: 'Publish',
};

export default function MyPermissions() {
  const { user } = useAuth();

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You must be logged in to view this page.</Alert>
      </Box>
    );
  }

  const permissions = user.effective_permissions;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <SecurityIcon color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h4">My Permissions</Typography>
          <Typography variant="body2" color="text.secondary">
            View your access levels in the Command Center
          </Typography>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1">
              <strong>Email:</strong> {user.email}
            </Typography>
            <Chip
              label={user.role}
              color={user.role === 'admin' ? 'error' : user.role === 'developer' ? 'primary' : 'default'}
              size="small"
            />
          </Box>
        </CardContent>
      </Card>

      {user.role === 'admin' ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          As an <strong>Admin</strong>, you have full access to all features in the Command Center.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          Your permissions are based on your <strong>{user.role}</strong> role.
          Contact an admin if you need additional access.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Feature</strong></TableCell>
              <TableCell align="center"><strong>View</strong></TableCell>
              <TableCell align="center"><strong>Edit</strong></TableCell>
              <TableCell align="center"><strong>Upload</strong></TableCell>
              <TableCell align="center"><strong>Sync</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(TAB_LABELS).map(([tabKey, label]) => {
              const tabPerms = permissions?.[tabKey as keyof UserPermissions] || {};

              // For admin, all permissions are true
              const isAdmin = user.role === 'admin';

              return (
                <TableRow key={tabKey} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {label}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <PermissionIndicator enabled={isAdmin || tabPerms.view} />
                  </TableCell>
                  <TableCell align="center">
                    {['dashboard', 'audit_log'].includes(tabKey) ? (
                      <Typography variant="caption" color="text.disabled">N/A</Typography>
                    ) : (
                      <PermissionIndicator enabled={isAdmin || tabPerms.edit} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {tabKey === 'onboarding_survey' ? (
                      <PermissionIndicator enabled={isAdmin || tabPerms.upload} />
                    ) : (
                      <Typography variant="caption" color="text.disabled">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {tabKey === 'service_discovery' ? (
                      <PermissionIndicator enabled={isAdmin || tabPerms.sync} />
                    ) : (
                      <Typography variant="caption" color="text.disabled">N/A</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function PermissionIndicator({ enabled }: { enabled?: boolean }) {
  return enabled ? (
    <Chip
      icon={<CheckIcon sx={{ fontSize: 16 }} />}
      label="Yes"
      size="small"
      color="success"
      variant="outlined"
      sx={{ minWidth: 70 }}
    />
  ) : (
    <Chip
      icon={<CloseIcon sx={{ fontSize: 16 }} />}
      label="No"
      size="small"
      color="default"
      variant="outlined"
      sx={{ minWidth: 70, opacity: 0.6 }}
    />
  );
}
